<template>
  <v-dialog @input="close" :value="isOpened" max-width="480">
    <v-card>
      <v-toolbar dark color="primary" class="elevation-1">
        <v-toolbar-title class="font-weight-bold">
          {{ dialogTitle }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pb-0 pt-6">
        <v-form ref="form">
          <v-row class="d-flex">
            <v-col :cols="isCoinsCategoryCode(selectedCategoryCode) ? 12 : 9">
              <v-text-field
                v-model="updates.name"
                :label="`${$t('generalAttributes.name')}*`"
                dense
                outlined
                hide-details="auto"
                :rules="nameRules"
                @keyup.enter="submit" />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-if="!isCoinsCategoryCode(selectedCategoryCode)"
                v-model.number="updates.quantity"
                :label="$t('generalAttributes.quantity')"
                :rules="quantityRules"
                type="number"
                dense
                outlined
                @keyup.enter="submit" />
            </v-col>
          </v-row>
          <h4 class="mb-4">
            1. {{ $t('entities.promotions.components.selectCategory') }}
          </h4>
          <v-select
            v-model="selectedCategoryCode"
            :items="categoryOptions"
            :label="$t('entities.categories.entity')"
            :disabled="isEditMode"
            :rules="categoryRules"
            dense
            outlined />
          <h4 v-if="isCategorySelected" class="mb-4">
            2. {{ finalStepInstructionText }}
          </h4>
          <ProductVariantPicker
            v-if="isVariantPickerVisible"
            :variants="variants"
            :value="selectedVariantIds"
            @selection-change="handleVariantSelection"
            class="mb-4" />
          <v-text-field
            v-else-if="isCoinsQuantityFieldVisible"
            v-model.number="updates.quantity"
            :label="$t('generalAttributes.quantity')"
            :rules="quantityRules"
            type="number"
            dense
            outlined
            @keyup.enter="submit">
            <template #prepend-inner>
              <CoinIcon class="mt-1" />
            </template>
            <template #append>
              <span class="mt-1">{{ coinPrice }}</span>
            </template>
          </v-text-field>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="justify-end">
        <v-btn text @click="close">
          {{ $t('miscellaneous.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn color="primary" dark @click="submit">
          {{ $t('miscellaneous.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ArenaProductsApi from '@/services/shop/ArenaProductsApi'
import CoinIcon from '@/components/CoinIcon.vue'
import ProductVariantPicker from './ProductVariantPicker.vue'

const COINS_CATEGORY_CODE = 'CNS'

const resetUpdates = (data = {}) => ({
  id: data.id || '',
  name: data.name || '',
  quantity: data.quantity || 1,
  variantIds: data.variantIds ? [...data.variantIds] : [],
})

const normalizeVariantId = (value) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') {
    const resolved =
      value?.arena_variant_id || value?.variant_id || value?.id || ''
    return resolved ? String(resolved) : ''
  }
  return String(value)
}

const cloneVariants = (variants = []) => {
  if (!Array.isArray(variants)) return []
  // Deduplicate variants by their normalized ID to prevent duplicate keys
  const uniqueVariantsMap = new Map()
  variants.forEach((variant) => {
    const variantId = normalizeVariantId(variant)
    if (variantId && !uniqueVariantsMap.has(variantId)) {
      uniqueVariantsMap.set(variantId, { ...variant })
    }
  })
  return Array.from(uniqueVariantsMap.values())
}

const extractVariantIdsFromGroup = (group = {}) => {
  if (!group) return []
  if (Array.isArray(group.variantIds) && group.variantIds.length > 0) {
    return group.variantIds.map((id) => normalizeVariantId(id)).filter(Boolean)
  }
  if (Array.isArray(group.variants) && group.variants.length > 0) {
    return group.variants
      .map((variant) => normalizeVariantId(variant))
      .filter(Boolean)
  }
  return []
}

const normalizeCategoryId = (value) => {
  if (!value && value !== 0) return ''
  return String(value)
}

const findCategoryIdByCode = (categories = [], code = '') => {
  if (!code) return ''
  const match = categories.find((category) => category.code === code)
  return normalizeCategoryId(match?.id || '')
}

const findCategoryCodeById = (categories = [], categoryId = '') => {
  if (!categoryId) return ''
  const normalizedId = normalizeCategoryId(categoryId)
  const match = categories.find(
    (category) => normalizeCategoryId(category.id) === normalizedId,
  )
  return match?.code || ''
}

const extractCategoryIdFromGroup = (group = {}) => {
  if (!group) return ''
  return normalizeCategoryId(group.category_id) || ''
}

const extractCategoryCodeFromVariants = (variants = []) => {
  if (!Array.isArray(variants)) return ''
  for (const variant of variants) {
    const code =
      variant?.category?.code ||
      variant?.category_code ||
      variant?.arena_category?.code ||
      variant?.arena_product?.category_code ||
      variant?.arena_product?.category?.code ||
      ''
    if (code) return code
  }
  return ''
}

const extractCategoryCodeFromGroup = (group = {}, categories = []) => {
  if (!group) return ''
  const directCategory =
    group.category_code ||
    group.categoryCode ||
    group.category?.code ||
    group.category ||
    ''
  if (directCategory) return directCategory
  const categoryId = extractCategoryIdFromGroup(group)
  if (categoryId) {
    return findCategoryCodeById(categories, categoryId) || ''
  }
  return extractCategoryCodeFromVariants(group.variants)
}

export default {
  name: 'PromotionGroupUpsertDialog',
  components: {
    CoinIcon,
    ProductVariantPicker,
  },
  props: {
    categories: { type: Array, default: () => [] },
    group: { type: Object, default: () => ({}) },
    isOpened: { type: Boolean, default: false },
    arenaCurrency: { type: Object, default: () => ({}) },
  },
  data() {
    const initialVariantIds = extractVariantIdsFromGroup(this.group)
    const initialCategoryCode = extractCategoryCodeFromGroup(
      this.group,
      this.categories,
    )
    const initialCategoryId =
      extractCategoryIdFromGroup(this.group) ||
      findCategoryIdByCode(this.categories, initialCategoryCode)
    return {
      updates: resetUpdates(this.group),
      selectedCategoryCode: initialCategoryCode,
      selectedCategoryId: initialCategoryId,
      variants: cloneVariants(this.group?.variants || []),
      isLoadingVariants: false,
      selectedVariantIds: [...initialVariantIds],
      pendingInitialVariantIds:
        initialCategoryCode && initialVariantIds.length
          ? [...initialVariantIds]
          : [],
      isRestoringFromGroup:
        initialCategoryCode && initialVariantIds.length ? true : false,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena?.id
    },
    categoryOptions() {
      return this.categories.map((it) => ({ value: it.code, text: it.name }))
    },
    categoryRules() {
      return [(value) => !!value || this.$t('validation.required')]
    },
    coinPrice() {
      if (!this.isCoinsQuantityFieldVisible) return ''
      if (!this.arenaCurrency?.id) return ''
      const coinsPerCurrency = this.arenaCurrency.coins_per_currency
      const quantity = this.updates.quantity ?? 0
      const total = quantity / coinsPerCurrency
      const roundedTotal = total.toFixed(2)
      const currency = this.arenaCurrency.symbol || this.arenaCurrency.code
      return `${roundedTotal} ${currency}`
    },
    dialogTitle() {
      const key = this.isEditMode
        ? 'entities.promotions.card.tooltips.editComponent'
        : 'entities.promotions.card.addComponent'
      return this.$t(key)
    },
    finalStepInstructionText() {
      return this.isCoinsCategoryCode(this.selectedCategoryCode)
        ? this.$t('entities.promotions.components.inputCoinQuantity')
        : this.$t('entities.promotions.components.selectAllVariants')
    },
    isCategorySelected() {
      return !!this.selectedCategoryCode
    },
    isCoinsQuantityFieldVisible() {
      return Boolean(
        this.selectedCategoryCode &&
          this.isCoinsCategoryCode(this.selectedCategoryCode),
      )
    },
    isEditMode() {
      return Boolean(this.group?.id || this.group?.tempId)
    },
    isVariantPickerVisible() {
      return Boolean(
        this.selectedCategoryCode &&
          !this.isCoinsCategoryCode(this.selectedCategoryCode),
      )
    },
    nameRules() {
      return [(value) => !!value || this.$t('validation.required')]
    },
    quantityRules() {
      return [
        (value) =>
          (value !== null && value !== undefined && value !== '') ||
          this.$t('validation.required'),
        (value) =>
          Number(value) >= 1 || this.$t('validation.min', { amount: 1 }),
      ]
    },
  },
  methods: {
    close() {
      this.$emit('dialog:close')
    },
    onClose() {
      this.updates = resetUpdates()
      this.selectedCategoryCode = ''
      this.selectedCategoryId = ''
      this.selectedVariantIds = []
      this.pendingInitialVariantIds = []
      this.isRestoringFromGroup = false
      this.variants = []
      this.resetForm()
    },
    onOpen() {
      this.loadGroupData(this.group)
    },
    resetForm() {
      if (this.$refs.form) this.$refs.form.resetValidation()
    },
    loadGroupData(group = {}) {
      const variantIds = extractVariantIdsFromGroup(group)
      const categoryCode = extractCategoryCodeFromGroup(group, this.categories)
      const categoryId =
        extractCategoryIdFromGroup(group) ||
        findCategoryIdByCode(this.categories, categoryCode)
      this.updates = resetUpdates(group)
      this.selectedVariantIds = [...variantIds]
      this.updates.variantIds = [...variantIds]
      this.pendingInitialVariantIds =
        categoryCode && variantIds.length ? [...variantIds] : []
      this.isRestoringFromGroup =
        categoryCode && variantIds.length ? true : false
      this.variants = cloneVariants(group?.variants || [])
      this.selectedCategoryCode = categoryCode || ''
      this.selectedCategoryId = categoryId || ''
      this.resetForm()
    },
    submit() {
      if (!this.$refs.form || !this.$refs.form.validate()) return
      const payload = {
        ...this.updates,
        category_code: this.selectedCategoryCode,
        category_id: this.selectedCategoryId,
        variantIds: [...this.selectedVariantIds],
        variants: this.getSelectedVariants(),
      }
      return this.$emit('promotion-group:upsert', payload)
    },
    isCoinsCategoryCode(code) {
      return code === COINS_CATEGORY_CODE
    },
    async handleCategorySelection(code) {
      const categoryCode = code || ''
      this.selectedCategoryId = findCategoryIdByCode(
        this.categories,
        categoryCode,
      )
      const shouldRestoreSelection =
        this.isRestoringFromGroup &&
        Boolean(categoryCode) &&
        this.pendingInitialVariantIds.length > 0
      if (!categoryCode) {
        this.selectedCategoryId = ''
        this.resetVariantsState()
        this.pendingInitialVariantIds = []
        this.isRestoringFromGroup = false
        return
      }
      if (!shouldRestoreSelection) {
        this.resetVariantsState()
        this.pendingInitialVariantIds = []
        this.isRestoringFromGroup = false
      }
      if (this.isCoinsCategoryCode(categoryCode)) {
        this.pendingInitialVariantIds = []
        this.isRestoringFromGroup = false
        await this.fetchArenaVariantsByCategory(categoryCode)
        this.selectCoinsVariant()
        return
      }
      await this.fetchArenaVariantsByCategory(categoryCode)
      if (shouldRestoreSelection) {
        this.restoreInitialVariantSelection()
        this.isRestoringFromGroup = false
      }
    },
    resetVariantsState() {
      this.variants = []
      this.selectedVariantIds = []
      this.updates.variantIds = []
    },
    async fetchArenaVariantsByCategory(categoryCode) {
      if (!this.arenaId || !categoryCode) return
      this.isLoadingVariants = true
      try {
        let page = 1
        let lastPage = 1
        const perPage = 200
        const allVariants = []
        do {
          const response = await ArenaProductsApi.list(this.arenaId, {
            category_code: categoryCode,
            page,
            per_page: perPage,
          })
          const responseData = response.data?.data || []
          allVariants.push(
            ...responseData.flatMap((product) => product.variants || []),
          )
          const metadata = response.data?.metadata || {}
          const totalEntries = metadata.count ?? metadata.total ?? 0
          const size = metadata.size ?? metadata.per_page ?? perPage
          lastPage =
            metadata.last_page ??
            metadata.pages ??
            (size ? Math.ceil(totalEntries / size) : page)
          page += 1
        } while (page <= lastPage)
        this.variants = cloneVariants(allVariants)
      } catch (error) {
        console.error('Failed to fetch arena variants', error)
        this.variants = []
      } finally {
        this.isLoadingVariants = false
      }
    },
    handleVariantSelection(selectedVariantIds) {
      this.selectedVariantIds = [...selectedVariantIds]
      this.updates.variantIds = [...this.selectedVariantIds]
      this.pendingInitialVariantIds = []
      this.isRestoringFromGroup = false
    },
    restoreInitialVariantSelection() {
      if (!this.pendingInitialVariantIds.length) return
      const availableIds = new Set(
        this.variants
          .map((variant) => normalizeVariantId(variant))
          .filter(Boolean),
      )
      const restoredSelection = availableIds.size
        ? this.pendingInitialVariantIds.filter((id) => availableIds.has(id))
        : [...this.pendingInitialVariantIds]
      this.selectedVariantIds = [...restoredSelection]
      this.updates.variantIds = [...this.selectedVariantIds]
      this.pendingInitialVariantIds = []
    },
    selectCoinsVariant() {
      const coinVariant = this.variants.find(
        (variant) => variant?.attributes?.type === 'coins',
      )
      const coinVariantId = coinVariant ? normalizeVariantId(coinVariant) : ''
      this.selectedVariantIds = coinVariantId ? [coinVariantId] : []
      this.updates.variantIds = [...this.selectedVariantIds]
    },
    getSelectedVariants() {
      if (!Array.isArray(this.variants) || !this.variants.length) return []
      const allowedIds = new Set(this.selectedVariantIds)
      if (!allowedIds.size) return []
      return this.variants
        .filter((variant) => allowedIds.has(normalizeVariantId(variant)))
        .map((variant) => ({ ...variant }))
    },
  },
  watch: {
    isOpened(value) {
      return value ? this.onOpen() : this.onClose()
    },
    selectedCategoryCode(value) {
      this.handleCategorySelection(value)
    },
    group: {
      deep: true,
      handler() {
        if (this.isOpened) {
          this.loadGroupData(this.group)
        }
      },
    },
  },
}
</script>
